import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridAtom } from "../../../../components/atoms/atom";
import { setting as userService } from "../../../../store/services";
import { showError } from "../../../../utils/method";
import { searchData } from "../../../../utils/search";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";

const USER_SOURCE = "existing";

function TabExistingUsers({
	existingMemberIds,
	selectedUsers,
	onSelectedUsersChange,
}) {
	const { t } = useTranslation();
	const gridRef = useRef(null);
	const [allUsers, setAllUsers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const isSyncingSelectionRef = useRef(false);

	const selectedExistingEmails = useMemo(
		() =>
			new Set(
				selectedUsers
					.filter((u) => u.source === USER_SOURCE)
					.map((u) => u.email?.toLowerCase())
					.filter(Boolean),
			),
		[selectedUsers],
	);

	const gridUsers = useMemo(() => {
		if (!search.trim()) {
			return allUsers;
		}
		return searchData(search, allUsers, [
			{ field: "firstName" },
			{ field: "lastName" },
			{ field: "email" },
			{ field: "company.name" },
		]);
	}, [allUsers, search]);

	useEffect(() => {
		const load = async () => {
			try {
				setLoading(true);
				const result = await userService.getUsers();
				const available = (result.items || []).filter(
					(user) => !existingMemberIds.includes(user.id),
				);
				setAllUsers(available);
			} catch (error) {
				showError(t, error);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [existingMemberIds, t]);

	const syncGridSelection = useCallback(() => {
		const api = gridRef.current?.getApi?.();
		if (!api) {
			return;
		}

		isSyncingSelectionRef.current = true;
		api.forEachNode((node) => {
			const email = node.data?.email?.toLowerCase();
			const shouldSelect = email && selectedExistingEmails.has(email);
			if (node.isSelected() !== shouldSelect) {
				node.setSelected(shouldSelect);
			}
		});
		isSyncingSelectionRef.current = false;
	}, [selectedExistingEmails]);

	useEffect(() => {
		syncGridSelection();
	}, [gridUsers, selectedExistingEmails, syncGridSelection]);

	const userColumns = useMemo(
		() => [
			{
				field: "firstName",
				headerName: t("users.first-name"),
				flex: 1,
				minWidth: 120,
			},
			{
				field: "lastName",
				headerName: t("users.last-name"),
				flex: 1,
				minWidth: 120,
			},
			{
				field: "email",
				headerName: t("users.email"),
				flex: 1,
				minWidth: 160,
			},
			{
				field: "company.name",
				headerName: t("users.company"),
				flex: 1,
				minWidth: 120,
				valueGetter: (params) => params.data?.company?.name || "-",
			},
			{
				field: "role.name",
				headerName: t("users.role"),
				flex: 0,
				minWidth: 90,
				valueGetter: (params) => params.data?.role?.name || "-",
			},
		],
		[t],
	);

	const rowSelection = useMemo(
		() => ({
			mode: "multiRow",
			checkboxes: true,
			headerCheckbox: true,
			enableClickSelection: true,
			enableSelectionWithoutKeys: true,
		}),
		[],
	);

	const onRowSelection = useCallback(
		(selectedRows) => {
			if (isSyncingSelectionRef.current) {
				return;
			}

			const selectedInView = new Set(
				(selectedRows || [])
					.map((u) => u.email?.toLowerCase())
					.filter(Boolean),
			);

			onSelectedUsersChange((prev) => {
				const external = prev.filter((u) => u.source !== USER_SOURCE);
				const existingNotInView = prev.filter(
					(u) =>
						u.source === USER_SOURCE &&
						!gridUsers.some(
							(g) =>
								g.email?.toLowerCase() === u.email?.toLowerCase(),
						),
				);
				const fromGrid = gridUsers
					.filter((u) => selectedInView.has(u.email?.toLowerCase()))
					.map((user) => ({
						email: user.email?.toLowerCase(),
						firstName: user.firstName,
						lastName: user.lastName,
						source: USER_SOURCE,
					}));

				return [...external, ...existingNotInView, ...fromGrid];
			});
		},
		[gridUsers, onSelectedUsersChange],
	);

	const handleGridReady = useCallback(() => {
		syncGridSelection();
	}, [syncGridSelection]);

	return (
		<div className="add-member-wizard__tab-panel">
			<TextFieldAtom
				className="add-member-wizard__search"
				placeholder={t("projects.members.search-users")}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
			/>
			<div className="add-member-wizard__grid modal-grid-350">
				<AgGridAtom
					ref={gridRef}
					columnDefs={userColumns}
					rowData={gridUsers}
					getRowId={(params) => String(params.data.id)}
					onRowSelection={onRowSelection}
					rowSelection={rowSelection}
					onGridReady={handleGridReady}
					suppressRowClickSelection={false}
					noRowsMessage={
						loading
							? t("common.loading")
							: t("users.no-available-users")
					}
				/>
			</div>
		</div>
	);
}

export default TabExistingUsers;
